<div class="seg-delete-merge-dialog">
  @if (isDeleting) {
    <app-rings-loader></app-rings-loader>
  }

  <!-- Step 1: choice -->
  @if (step === 'choice') {
    <div class="dialog-header">
      <h1 class="dialog-title">{{ 'Archive SEG' | translate }}</h1>
      <button class="icon-btn" (click)="cancel()" [attr.aria-label]="'Close dialog' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="dialog-body">
      <div class="option-list" role="radiogroup">
        <label class="option" [class.selected]="selectedChoice === 'delete'" (click)="selectChoice('delete')">
          <input type="radio" name="seg-delete-choice" [checked]="selectedChoice === 'delete'" />
          <span class="option-copy">
            <strong>{{ 'Archive this SEG' | translate }}</strong>
            <span>{{ '{{name}} moves to the archived list, where you can restore it at any time. Its samples and their history are unaffected.' | translate: { name: data.segName } }}</span>
          </span>
        </label>
        <label class="option merge-option" [class.selected]="selectedChoice === 'merge'" (click)="selectChoice('merge')">
          <input type="radio" name="seg-delete-choice" [checked]="selectedChoice === 'merge'" />
          <span class="option-copy">
            <strong>
              {{ 'Merge into another SEG' | translate }}
              <span class="consequence-flag">{{ 'Redirects data' | translate }}</span>
            </strong>
            <span>{{ "Redirects {{name}}'s statistics, reports, and views to a SEG you choose next. This can't be easily undone." | translate: { name: data.segName } }}</span>
          </span>
        </label>
      </div>
    </div>
    <div class="dialog-footer">
      <button class="btn btn-secondary" [disabled]="isDeleting" (click)="cancel()">{{ 'Cancel' | translate }}</button>
      <button class="btn btn-primary" [disabled]="!canContinueChoice || isDeleting" (click)="continueFromChoice()">{{ 'Continue' | translate }}</button>
    </div>
  }

  <!-- Step 2: target picker -->
  @if (step === 'picker') {
    <div class="dialog-header">
      <div class="titles">
        <p class="eyebrow">{{ 'Merge SEG' | translate }}</p>
        <h1 class="dialog-title">{{ 'Choose a target SEG' | translate }}</h1>
      </div>
      <button class="icon-btn" (click)="cancel()" [attr.aria-label]="'Close dialog' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    @if (isLoadingCandidates) {
      <app-rings-loader></app-rings-loader>
    }
    @if (!isLoadingCandidates) {
      @if (candidates.length > 0) {
        <div class="dialog-body">
          <div class="search-box">
            <mat-icon>search</mat-icon>
            <input type="text" [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange()"
              [placeholder]="'Search SEGs' | translate" [attr.aria-label]="'Search SEGs' | translate" />
            </div>
            <p class="helper-text">{{ "Showing active SEGs at this organization level that aren't already merged into another SEG." | translate }}</p>
            @if (filteredCandidates.length > 0) {
              <div class="candidate-list" role="radiogroup">
                @for (candidate of filteredCandidates; track trackById($index, candidate)) {
                  <label class="candidate"
                    [class.selected]="selectedTarget?.id === candidate.id" (click)="selectCandidate(candidate)">
                    <input type="radio" name="seg-merge-target" [checked]="selectedTarget?.id === candidate.id" />
                    <span class="cand-copy">
                      <strong [title]="candidate.name">{{ candidate.name }}</strong>
                      <span class="meta">{{ 'Code' | translate }}: {{ candidate.code ?? '-' }} &middot; {{ candidate.organizationLevelName }}</span>
                    </span>
                  </label>
                }
              </div>
            } @else {
              <p class="no-results">{{ 'No results for "{{query}}"' | translate: { query: searchQuery } }}</p>
            }
          </div>
          <div class="dialog-footer">
            <button class="btn btn-ghost" (click)="backFromPicker()">{{ 'Back' | translate }}</button>
            <span class="spacer"></span>
            <button class="btn btn-secondary" (click)="cancel()">{{ 'Cancel' | translate }}</button>
            <button class="btn btn-primary" [disabled]="!canContinuePicker" (click)="continueFromPicker()">{{ 'Continue' | translate }}</button>
          </div>
        } @else {
          <div class="dialog-body">
            <div class="empty-state">
              <div class="icon-wrap">
                <mat-icon>search_off</mat-icon>
              </div>
              <h2>{{ 'No eligible SEGs to merge into' | translate }}</h2>
              <p>{{ 'There are no other active, unmerged SEGs at this organization level to merge {{name}} into.' | translate: { name: data.segName } }}</p>
            </div>
          </div>
          <div class="dialog-footer">
            <button class="btn btn-ghost" (click)="backFromPicker()">{{ 'Back' | translate }}</button>
            <span class="spacer"></span>
            <button class="btn btn-secondary" [disabled]="isDeleting" (click)="deleteInsteadFromEmptyPicker()">{{ 'Archive instead' | translate }}</button>
          </div>
        }
      }
    }

    <!-- Step 3: confirm merge -->
    @if (step === 'confirm') {
      <div class="dialog-header">
        <div class="titles">
          <mat-icon class="warn-icon">warning</mat-icon>
          <h1 class="dialog-title confirm-names">
            {{ 'Merge {{name}} into' | translate: { name: data.segName } }}
            <span class="arrow">&rarr;</span>
            {{ selectedTarget?.name }}?
          </h1>
        </div>
        <button class="icon-btn" (click)="cancel()" [attr.aria-label]="'Close dialog' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      @if (hasError) {
        <div class="error-banner" role="alert">
          <mat-icon>error_outline</mat-icon>
          <span>{{ "Couldn't merge {{name}} right now. Your selection is unchanged — try Merge SEG again." | translate: { name: data.segName } }}</span>
        </div>
      }
      <div class="dialog-body confirm-body">
        <p class="consequence">{{ "Merging moves {{sourceName}}'s statistics, reports, and views to {{targetName}}. From this point on, {{sourceName}}'s exposure history will appear under {{targetName}}'s name everywhere it's used." | translate: { sourceName: data.segName, targetName: $safeNavigationMigration(selectedTarget?.name) } }}</p>
        <p class="qualifier">
          <mat-icon>info</mat-icon>
          <span>{{ 'Existing risk assessments are not changed. Only risk assessments created after the merge will include {{name}}\'s samples.' | translate: { name: data.segName } }}</span>
        </p>
        <p class="irreversible">{{ 'This cannot be easily undone.' | translate }}</p>
      </div>
      <div class="dialog-footer">
        <button class="btn btn-ghost" [disabled]="isSaving" (click)="backFromConfirm()">{{ 'Back' | translate }}</button>
        <span class="spacer"></span>
        <button class="btn btn-secondary" [disabled]="isSaving" (click)="cancel()">{{ 'Cancel' | translate }}</button>
        <button class="btn btn-danger" [disabled]="isSaving" (click)="confirmMerge()">
          @if (isSaving) {
            <span class="spinner"></span>
          }
          {{ (isSaving ? 'Merging…' : 'Merge SEG') | translate }}
        </button>
      </div>
    }
  </div>
